import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { I18NextPipe } from 'angular-i18next';
import { cloneDeep } from 'lodash';
import { MbsPopupType, MbsSize, ModalService, ModalSettings, Toast, ToastService } from 'mbs-ui-kit';
import { convertAgentTypeToNeededCase } from 'mbs-ui-shared@models/Computer';
import { AsyncRemoteCommandService } from 'mbs-ui-shared@services/async-remote-command/async-remote-command.service';
import { BehaviorSubject, Observable, Subject, debounceTime, noop } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export class GroupOperationIndicator {
  private $finish = new Subject<void>();
  private $failed = new Subject<void>();
  private $modalClosed = new Subject<void>();
  private silent = false;
  private modalIsOpened = false;
  private modalInstance: NgbModalRef;
  private modalContext: {
    ok: boolean;
    message: string;
    totalComputers?: number;
    isGroupAction?: boolean;
    resultList: Array<{ displayName: string; name: string }>;
    isErrorsInResultList: boolean;
  };

  constructor(
    private toast: ToastService,
    private modalService: ModalService,
    private i18nPipe: I18NextPipe,
    private config: { operationText: string; isGroupAction: boolean; detailModal: any },
    private untilDestroyed: <U>(source?: Observable<U>) => Observable<U>
  ) {}

  indicate(
    fullData: Observable<unknown>,
    intermediateData: typeof AsyncRemoteCommandService.prototype.lastAsyncResult
  ): Observable<boolean> {
    const result = new BehaviorSubject(false);

    (fullData as Observable<any>).pipe(takeUntil(this.$failed), this.untilDestroyed).subscribe({
      next: (data) => {
        result.next(true);

        !!data?.resultList && this.displayInfo(data, true);
      },
      error: noop
    });

    intermediateData
      .pipe(debounceTime(100), takeUntil(this.$failed), takeUntil(this.$finish), takeUntil(this.$modalClosed), this.untilDestroyed)
      .subscribe({
        next: (data) => {
          result.next(true);

          !!data?.response.resultList &&
            this.displayInfo({ ...(data.response as typeof this.modalContext), totalComputers: data.totalComputers });
        },
        error: noop
      });

    return result;
  }

  private displayInfo(data: typeof this.modalContext, full?: boolean): void {
    if (full) {
      delete this.silent;
      this.$finish.next();
      data.totalComputers = data.resultList.length;
    }

    const prepareState = this.prepareData(data);

    if (prepareState && prepareState.ok) {
      return void (prepareState.isErrorsInResultList
        ? this.toastFailGroupOperation(prepareState)
        : this.toastSuccessGroupOperation(prepareState));
    }

    this.$failed.next();
    this.openToast(
      prepareState,
      {
        header: this.config.operationText,
        content: this.i18nPipe.transform('shared:computers.forceUpdate.groupOperation.operationFailed', {
          operation: this.config.operationText
        }),
        type: MbsPopupType.danger
      },
      true
    );
  }

  private prepareData(data: typeof this.modalContext): typeof this.modalContext {
    const prepareState = cloneDeep(data);
    const newResults = [];

    prepareState.resultList.forEach((item: any) => {
      const found = newResults.find((res) => res && res.hid === item.hid);

      if (!found) {
        item.childs = [{ ...item.result, agentType: convertAgentTypeToNeededCase(item.agentType) }];

        return void newResults.push(item);
      }

      found.childs.push({ ...item.result, agentType: convertAgentTypeToNeededCase(item.agentType) });
    });

    prepareState.resultList = newResults;

    return prepareState;
  }

  private toastSuccessGroupOperation(prepareState: typeof this.modalContext): void {
    const compUpdateResult = prepareState.resultList;
    const countOfComputers = this.config.isGroupAction ? compUpdateResult.length : 1;

    if (!this.config.isGroupAction && !compUpdateResult.length) {
      return;
    }

    const getContentText = (): { text: string; hideOpen?: boolean } => {
      if (this.config.isGroupAction) {
        if (!countOfComputers) {
          return {
            text: this.i18nPipe.transform('shared:computers.forceUpdate.groupOperation.operationStarted', {
              operation: this.config.operationText
            })
          };
        }

        return {
          text: this.i18nPipe.transform('shared:computers.forceUpdate.groupOperation.operationForComputerStarted', {
            computerPlural: this.i18nPipe.transform('shared:computers.computer', { count: countOfComputers }),
            operation: this.config.operationText,
            countOfComputers
          })
        };
      }

      const name = compUpdateResult[0].name;
      const displayName = compUpdateResult[0].displayName;
      const nameT = name.trim().toLowerCase();
      const displayNameT = displayName?.trim().toLowerCase();

      return {
        text: this.i18nPipe.transform('shared:computers.forceUpdate.groupOperation.operationForSingleComputerStarted', {
          operation: this.config.operationText,
          compName: displayNameT !== nameT ? `${displayName} [${name}]` : name
        }),
        hideOpen: true
      };
    };

    const config = getContentText();

    this.openToast(
      prepareState,
      {
        header: this.config.operationText,
        content: config.text,
        type: this.config.isGroupAction ? MbsPopupType.info : MbsPopupType.success,
        icon: true
      },
      config.hideOpen
    );
  }

  private toastFailGroupOperation(prepareState: typeof this.modalContext): void {
    this.openToast(prepareState, {
      header: this.config.operationText,
      content: prepareState.message,
      type: MbsPopupType.danger,
      icon: true
    });
  }

  private openToast(prepareState: typeof this.modalContext, config: Partial<Toast>, hideOpen?: boolean): void {
    if (!this.modalIsOpened && !this.silent) {
      const buttons: typeof Toast.prototype.buttons = [
        { clickHandler: () => this.handleOpenModalWithDetail(prepareState, this.config.operationText) }
      ];

      this.silent = true;
      this.toast.toast(new Toast({ ...config, buttons: hideOpen ? [] : buttons }));
    }

    this.updateModalWithDetail(prepareState);
  }

  private updateModalWithDetail(content: typeof this.modalContext): void {
    if (this.modalIsOpened) {
      this.modalContext = { ...content, isGroupAction: this.config.isGroupAction };
      this.modalInstance.componentInstance.data = this.modalContext;
    }
  }

  private handleOpenModalWithDetail(content: typeof this.modalContext, title: string): void {
    if (this.modalIsOpened) {
      return;
    }

    const fullTitle = `${title} ${this.i18nPipe.transform('shared:common.details')}`;
    const settings: ModalSettings = {
      collapsing: true,
      header: {
        title: fullTitle,
        showExpandedCross: true,
        textOverflow: true
      },
      size: MbsSize.lg
    };

    this.modalIsOpened = true;
    this.modalContext = { ...content, isGroupAction: this.config.isGroupAction };
    this.modalInstance = this.modalService.openRef(this.config.detailModal, settings);
    this.modalInstance.componentInstance.title = fullTitle;
    this.modalInstance.componentInstance.data = this.modalContext;
    this.modalInstance.result.finally(() => {
      delete this.modalIsOpened;
      delete this.modalContext;
      delete this.modalInstance;

      this.$modalClosed.next();
    });
  }
}
